import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ACTION_PROCESS_TASK_FINDER} from 'notebook/store/VersioningConstants';
import makeSimpleActionFunction from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {performClientSideWriteAction} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {
  BaseActionFunctionParameters,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  isProcessTaskIdsContext,
  ProcessTaskIdsContext,
} from 'notebook/structured-tables/StructuredTableOutsideNotebookConstants';
import getAllStructuredTablesInNote from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import TableUtil from 'notebook/tables/TableUtil';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import notifications from 'notifications';
import {Schema} from 'schemas/SchemaConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import {withLoadingScreen} from 'util/Promises';

import * as ProcessManagementDAO from '../../dao/ProcessManagementDAO';
import {OneTaskFinderResult} from '../../dao/ProcessManagementDAO';
import {makeTaskIdCell} from '../MakeProcessCellUtils';
import ProcessOutputStructuredTableAdapter, {
  getOutputSchema,
  ProcessOutputStructuredTable,
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata,
} from '../ProcessOutputStructuredTableAdapter';
import ProcessTaskStructuredTableAdapter, {
  ProcessTaskStructuredTable,
} from '../ProcessTaskStructuredTableAdapter';
import TaskFinderErrorModal, {
  TaskFinderError,
  TaskFinderErrorCode,
  TaskFinderNotConfiguredErrorModal,
} from '../TaskFinderErrorModal';
import {
  FetchOutputSchemaTaskFinderQuery,
  FetchOutputSchemaTaskFinderQueryVariables,
} from './__generated/FetchOutputSchemaTaskFinderQuery';

type LookupConfig = NonNullable<
  ExtractByTypename<
    FetchOutputSchemaTaskFinderQuery['processOutputSchema'],
    'ProcessOutputSchemaNode'
  >['taskFinderLookupConfig']
>;

export const FETCH_OUTPUT_SCHEMA_TASK_FINDER_QUERY = gql`
  query FetchOutputSchemaTaskFinderQuery($outputSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processOutputSchema(id: $outputSchemaId) {
      ... on ProcessOutputSchemaNode {
        id
        taskFinderLookupConfig {
          id
          isMulti
          lookupSteps
        }
      }

      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function getTaskFinderLookupConfig(outputSchemaId: string): Promise<LookupConfig | null> {
  const data = await doQuerySafe<FetchOutputSchemaTaskFinderQuery, FetchOutputSchemaTaskFinderQueryVariables>(
    {query: FETCH_OUTPUT_SCHEMA_TASK_FINDER_QUERY},
    {variables: {outputSchemaId}}
  );
  return !isInaccessible(data.processOutputSchema)
    ? (data.processOutputSchema.taskFinderLookupConfig ?? null)
    : null;
}

function getLookupBaseColumnIndex(
  structuredTable: ProcessOutputStructuredTable,
  outputSchema: Schema,
  taskFinderLookupConfig: LookupConfig
): number | null {
  const schemaFieldApiId = taskFinderLookupConfig.lookupSteps?.[0].schemaField;
  assert(schemaFieldApiId != null, 'Expected first task finder lookup step to be a SCHEMA_FIELD step');
  const schemaField = outputSchema.allFields.find((field) => field.apiId === schemaFieldApiId);
  assert(
    schemaField != null,
    "Expected first task finder lookup's schema field to match an output schema field"
  );
  if (schemaField == null) {
    const outputSchemaFields = outputSchema.allFields;
    logging.warn(`Unable to find schema field ${schemaFieldApiId} in output schema ${outputSchema.name}`, {
      extra: {outputSchemaFields},
    });
  }
  const lookupBaseColIdx = structuredTable.columns.findIndex(
    (col) => col.schemaFieldApiId === schemaFieldApiId
  );
  if (lookupBaseColIdx < 0) {
    notifications.error(
      `Could not find column corresponding to schema field '${schemaField.displayName}' on '${outputSchema.name}'.`
    );
    return null;
  }
  return lookupBaseColIdx;
}

export function getTaskFinderRequestRows(
  computedTable: Array<Array<EvaluatedCell>>,
  structuredTable: ProcessOutputStructuredTable,
  outputSchema: Schema,
  taskFinderLookupConfig: LookupConfig,
  taskColIdx: number
): Array<{rowId: string; typedValue: TypedValue}> | null {
  assert(taskColIdx >= 0, 'Expected table to have a PROCESS_TASK column');
  const lookupBaseColIdx = getLookupBaseColumnIndex(structuredTable, outputSchema, taskFinderLookupConfig);
  if (lookupBaseColIdx == null) {
    return null;
  }
  const rows: Array<{rowId: string; typedValue: TypedValue}> = [];
  for (let rowIdx = 0; rowIdx < computedTable.length; rowIdx++) {
    if (isTypedValueEmpty(computedTable[rowIdx][taskColIdx].value)) {
      rows.push({
        rowId: structuredTable.rows[rowIdx].rowId,
        typedValue: computedTable[rowIdx][lookupBaseColIdx].value,
      });
    }
  }
  return rows;
}

export async function doTaskFinder({
  structuredTable,
  structuredTableAdapter,
  updateHandler,
  note,
  computedTable,
  outsideNotebookContext,
}: BaseActionFunctionParameters<
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata
> & {outsideNotebookContext?: ProcessTaskIdsContext | null}): Promise<void> {
  const processOutputStructuredTableAdapter = structuredTableAdapter as ProcessOutputStructuredTableAdapter;
  const outputSchema = processOutputStructuredTableAdapter.getOutputSchema(structuredTable);
  assert(
    outputSchema != null,
    'Task finder action function should not be available if output schema is inaccessible'
  );
  const taskFinderLookupConfig = await getTaskFinderLookupConfig(outputSchema.id);
  if (taskFinderLookupConfig == null) {
    await LoadingScreen.withLoadingScreenHidden(async () => {
      await legacyRunModal(TaskFinderNotConfiguredErrorModal, {processOutputSchema: outputSchema});
    });
    return;
  }

  let availableTaskIds;
  if (note != null) {
    const structuredTables = getAllStructuredTablesInNote(note);
    const taskTable = structuredTables.find(
      (structuredTable) => structuredTable.kind === StructuredTableKind.PROCESS_TASK
    ) as ProcessTaskStructuredTable;
    assert(taskTable != null, 'Expected a task table in entry');
    const taskTableAdapter = ProcessTaskStructuredTableAdapter.getAdapter();
    availableTaskIds = taskTableAdapter.getAllTaskIds(taskTable);
  } else if (outsideNotebookContext != null) {
    availableTaskIds = outsideNotebookContext.taskIds;
  } else {
    assert(false, 'Expected task finder to be run with note or task IDs context');
  }
  const taskColIdx = structuredTable.columns.findIndex(
    (col) => col.columnType === TableColumnType.PROCESS_TASK
  );
  const rows = getTaskFinderRequestRows(
    computedTable,
    structuredTable,
    outputSchema,
    taskFinderLookupConfig,
    taskColIdx
  );
  if (rows == null) {
    return;
  }
  if (availableTaskIds.length === 0) {
    notifications.warn('No tasks were found in the associated task table.', {id: 'task-finder'});
    return;
  }
  const taskFinderResponse = await ProcessManagementDAO.runTaskFinder({
    processOutputSchemaId: outputSchema.id,
    availableTaskIds,
    rows,
  });
  const foundTasksByRow: {[id: string]: OneTaskFinderResult} = {};
  for (const foundTask of taskFinderResponse.foundTasks) {
    foundTasksByRow[foundTask.rowId] = foundTask;
  }
  const lookupBaseColumnIdx = getLookupBaseColumnIndex(structuredTable, outputSchema, taskFinderLookupConfig);
  // Since we were able to successfully run task finder, the base lookup column must exist.
  assert(lookupBaseColumnIdx != null, 'Expected lookupBaseColumnIdx to be non-null');
  let updatedCells: Array<Array<Cell>> = [];
  const taskFinderErrors: Array<TaskFinderError> = [];
  structuredTable.rows.forEach(({rowId, cells}, idx) => {
    // Should not mutate the original cells and instead rely on the updateHandler to patch in the changes.
    const newCells = cells.map((cell) => ({...cell}));
    const foundTasks: Array<{id: string; humanId: string}> | null = foundTasksByRow[rowId]?.tasks ?? null;
    const errors: Array<string> | null = foundTasksByRow[rowId]?.errors ?? null;
    if (errors != null) {
      taskFinderErrors.push({
        rowId,
        rowIdx: idx,
        errors,
        errorCode: TaskFinderErrorCode.LOOKUP_ERROR,
        tasks: foundTasks,
      });
    } else if (foundTasks != null) {
      if (foundTasks.length === 0) {
        const errorMessage = TableUtil.isCellEmpty(cells[lookupBaseColumnIdx])
          ? `No tasks found. Please link manually or fill in the ${structuredTable.columns[lookupBaseColumnIdx].name} column.`
          : 'No tasks found. Please link manually or update the lineage.';
        taskFinderErrors.push({
          rowId,
          rowIdx: idx,
          errorCode: TaskFinderErrorCode.NO_TASKS_FOUND,
          errors: [errorMessage],
          tasks: foundTasks,
        });
      } else if (foundTasks.length > 1) {
        taskFinderErrors.push({
          rowId,
          rowIdx: idx,
          errorCode: TaskFinderErrorCode.MULTIPLE_TASKS_FOUND,
          errors: ['Multiple tasks found. The output will be split into one row per task.'],
          tasks: foundTasks,
        });
      } else {
        newCells[taskColIdx] = makeTaskIdCell({id: foundTasks[0].id, humanId: foundTasks[0].humanId});
      }
    }
    updatedCells.push(newCells);
  });

  if (taskFinderErrors.length > 0) {
    await LoadingScreen.withLoadingScreenHidden(async () => {
      updatedCells =
        (await legacyRunModal(TaskFinderErrorModal, {
          errors: taskFinderErrors,
          taskIdColumnIdx: taskColIdx,
          currentCells: updatedCells,
        })) ?? updatedCells;
    });
  }

  updateHandler({
    updatedCells,
    columnUpdates: {},
  });
}

export function onInvokeTaskFinder(
  params: BaseActionFunctionParameters<
    ProcessOutputStructuredTableMetadata,
    ProcessOutputStructuredTableRowMetadata
  >
): Promise<void> {
  if (params.entryOrTemplateId != null) {
    assert(
      APIUtil.isBaseEntry(params.entryOrTemplateId),
      `Task finder action is only supported in entries or execution context. Got entryOrTemplateId=${params.entryOrTemplateId}`
    );
    assert(params.note != null, 'Task finder in entry requires a note.');
    assert(params.outsideNotebookContext == null, 'Cannot provide outsideNotebookContext in entry');
  } else {
    assert(
      params.outsideNotebookContext != null && isProcessTaskIdsContext(params.outsideNotebookContext),
      'Task finder action is only supported in entries or execution context.'
    );
    assert(params.note == null, 'Cannot provide note outside entry');
  }

  if (params.entryOrTemplateId) {
    return performClientSideWriteAction({
      entryOrTemplateId: params.entryOrTemplateId,
      loadingScreenMessage: 'Finding tasks...',
      autoSaverAction: ACTION_PROCESS_TASK_FINDER,
      doPerformAction: async () => {
        await doTaskFinder({...params, outsideNotebookContext: null});
      },
      analyticsEvent: ELN_ANALYTICS_ACTIONS.RAN_PROCESS_TASK_FINDER,
      analyticsMetadata: {structuredTableKind: params.structuredTable.kind},
    });
  } else {
    return withLoadingScreen(
      doTaskFinder({
        ...params,
        outsideNotebookContext: params.outsideNotebookContext as ProcessTaskIdsContext,
      }),
      'Finding tasks'
    );
  }
}

export function isTaskFinderDisabled({
  structuredTable,
  locked,
  entryOrTemplateId,
  note,
  outsideNotebookContext,
}: BaseActionFunctionParameters<
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata
>): boolean {
  if (locked || structuredTable.metadata?.isReadOnly || getOutputSchema(structuredTable.schemas) == null) {
    return true;
  }
  if (entryOrTemplateId != null) {
    return !APIUtil.isBaseEntry(entryOrTemplateId) || note == null;
  } else {
    return outsideNotebookContext == null || !isProcessTaskIdsContext(outsideNotebookContext);
  }
}

const taskFinderActionFunction = makeSimpleActionFunction<ProcessOutputStructuredTableMetadata, {}>({
  label: 'Link output to task',
  icon: 'format-link',
  iconAltText: 'Link output to task',
  getDisabled: isTaskFinderDisabled,
  buttonDesign: {
    buttonStyle: ButtonStyle.TERTIARY_GRAY,
  },
  onInvoke: onInvokeTaskFinder,
  getHidden: ({structuredTable, structuredTableAdapter}) => {
    const outputSchema = (structuredTableAdapter as ProcessOutputStructuredTableAdapter).getOutputSchema(
      structuredTable
    );
    return outputSchema?.taskFinderLookupConfig == null;
  },
});

export default taskFinderActionFunction;
